'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { getSession, type SessionUser } from './api';

/**
 * The signed-in user, for rendering decisions: which nav items to show, where
 * to redirect.
 *
 * It prefers what the server already knew. A panel page is rendered by a server
 * component that has the cookie in its hands, so the first HTML can carry the
 * name and the role; {@link SessionProvider} hands that down and this hook uses
 * it. Nothing is stored in the browser: local storage would be a second copy of
 * an answer the server gives on every request, going stale in a place we cannot
 * see.
 *
 * With no provider above it, it falls back to asking. That is the sign-in page
 * and anything else outside the panel, where there is no session to draw yet.
 */
export function useSession(): { loading: boolean; user: SessionUser | null } {
  const seeded = useContext(SessionContext);
  const [state, setState] = useState<{ loading: boolean; user: SessionUser | null }>(() =>
    seeded === undefined ? { loading: true, user: null } : { loading: false, user: seeded },
  );

  useEffect(() => {
    if (seeded !== undefined) {
      setState({ loading: false, user: seeded });
      return;
    }

    let alive = true;
    getSession().then((user) => {
      if (alive) setState({ loading: false, user });
    });
    return () => {
      alive = false;
    };
  }, [seeded]);

  return state;
}

/**
 * Carries the session the server already resolved down to the components that
 * decide what to draw. `undefined` means "nobody told us", which is how the
 * hook knows to ask for itself.
 */
const SessionContext = createContext<SessionUser | null | undefined>(undefined);

export function SessionProvider({
  user,
  children,
}: {
  /** `undefined` means nobody could answer, so the hook asks for itself. */
  user: SessionUser | null | undefined;
  children: React.ReactNode;
}) {
  return <SessionContext.Provider value={user}>{children}</SessionContext.Provider>;
}
